/**
 * Small chat icon for the customer header. Opens the CustomerChatBubble
 * drawer via a custom window event. Poll for unread count every 45s so the
 * badge stays fresh even when the drawer isn't mounted or open.
 *
 * Ship: Feb 2026 (choice 1c — bubble + header icon on customer side).
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { MessageCircle } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const POLL_MS = 45000;

export default function CustomerChatHeaderIcon({ user }) {
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    if (!user || user.role !== 'customer') return;
    try {
      const { data } = await axios.get(`${API_URL}/api/customer/chat`, { withCredentials: true });
      setUnread(data?.thread?.unread_customer_count || 0);
    } catch {
      /* silent */
    }
  }, [user]);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  if (!user || user.role !== 'customer') return null;

  return (
    <Button
      variant="outline"
      onClick={() => window.dispatchEvent(new CustomEvent('openCustomerChat'))}
      className="relative border-[#2A0F3D] text-[#39FF14] hover:border-[#39FF14] hover:bg-[rgba(57,255,20,0.1)] rounded-none px-2 sm:px-3"
      title="Chat"
      data-testid="customer-chat-header-icon"
      aria-label="Open chat"
    >
      <MessageCircle className="w-4 h-4 sm:mr-2" />
      <span className="hidden sm:inline">CHAT</span>
      {unread > 0 && (
        <span
          className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-[#FF3B30] text-white text-[10px] font-bold rounded-full flex items-center justify-center"
          data-testid="customer-chat-header-unread-badge"
        >
          {unread > 9 ? '9+' : unread}
        </span>
      )}
    </Button>
  );
}
